/**
 * The season replay timeline.
 *
 * The API sends the season's chapter reads grouped by read date, in the order
 * they happened. Here that becomes one flat event list plus a calendar of
 * every day from the first day anyone read through today (days with no
 * reading still take their turn, so the date counter never jumps). Empty days
 * before the first reading are skipped, so playback opens on the action.
 *
 * The playhead is measured in days: 2.5 means halfway through the third day.
 * Each day plays for the same length of time and its reads are spaced evenly
 * across it, so a busy evening reads as a burst instead of a blur.
 */

// Aim for a replay of about this long, but never rush a day below the minimum.
const TARGET_REPLAY_MS = 30000;
const MIN_DAY_MS = 300;
const MAX_DAY_MS = 3000;

/** YYYY-MM-DD for the day after `isoDate`, without local-timezone drift. */
function nextIsoDate(isoDate) {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

/**
 * @param {{ seasonStart: string, today?: string, days: { date: string, chapters: number[] }[] }} data
 * @returns {{ dates: string[], events: number[], dayStart: number[] }}
 *   `dayStart[i]` is the index of day i's first event; `dayStart[dates.length]`
 *   is the total event count.
 */
export function buildReplay({ seasonStart, today, days = [] }) {
  const byDate = new Map(days.map(day => [day.date, day.chapters]));
  const firstReadDate = days.length > 0 ? days[0].date : seasonStart;
  const start = firstReadDate > seasonStart ? firstReadDate : seasonStart;
  const lastReadDate = days.length > 0 ? days[days.length - 1].date : seasonStart;
  const end = today && today > lastReadDate ? today : lastReadDate;

  const dates = [];
  const events = [];
  const dayStart = [];
  // A season start after the last read (no reading yet) still gets one day.
  for (let date = start; dates.length === 0 || date <= end; date = nextIsoDate(date)) {
    dates.push(date);
    dayStart.push(events.length);
    const chapters = byDate.get(date);
    if (chapters) events.push(...chapters);
  }
  dayStart.push(events.length);

  return { dates, events, dayStart };
}

/** How long one day plays at 1x, so the whole season lands near 30 seconds. */
export function dayDurationMs(dayCount) {
  if (!dayCount) return MAX_DAY_MS;
  return Math.min(MAX_DAY_MS, Math.max(MIN_DAY_MS, TARGET_REPLAY_MS / dayCount));
}

/** Number of events that have played by playhead `t` (in days). */
export function eventIndexAt(replay, t) {
  const dayCount = replay.dates.length;
  if (t <= 0) return 0;
  if (t >= dayCount) return replay.events.length;
  const day = Math.floor(t);
  const start = replay.dayStart[day];
  const count = replay.dayStart[day + 1] - start;
  return start + Math.floor((t - day) * count);
}

/** Index into `dates` of the day the playhead is on (the last day at the end). */
export function dayIndexAt(replay, t) {
  return Math.min(replay.dates.length - 1, Math.max(0, Math.floor(t)));
}

/**
 * Move `counts` ({ [chapterId]: reads }) from having played `from` events to
 * having played `to`, in place. Works in both directions, so scrubbing back
 * costs only the events crossed.
 */
export function moveCounts(counts, events, from, to) {
  if (to > from) {
    for (let i = from; i < to; i += 1) {
      const id = events[i];
      counts[id] = (counts[id] || 0) + 1;
    }
  } else {
    for (let i = from - 1; i >= to; i -= 1) {
      const id = events[i];
      if (counts[id] > 1) counts[id] -= 1;
      else delete counts[id];
    }
  }
  return counts;
}

/** Counts after the first `index` events, from scratch. */
export function countsAt(events, index) {
  return moveCounts({}, events, 0, index);
}

/**
 * Headline numbers for the frame: chapter reads so far, distinct chapters
 * read, and how many times the whole Bible has been read through.
 */
export function summarizeFrame(counts, totalChapters, index) {
  const ids = Object.keys(counts);
  let completions = 0;
  if (totalChapters > 0 && ids.length >= totalChapters) {
    completions = Math.min(...ids.map(id => counts[id]));
  }
  return {
    chaptersRead: index,
    uniqueChapters: ids.length,
    unreadChapters: Math.max(0, totalChapters - ids.length),
    completions
  };
}

/** "Tue, Oct 14" for a YYYY-MM-DD date, in the calendar sense (no TZ shift). */
export function formatReplayDate(isoDate) {
  const [year, month, day] = String(isoDate).split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}
